import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { Dropzone } from '@/components/dropzone';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { useNotification } from '@/components/notifications';
import {
  uploadSources,
  fileIdOf,
  UploadProgressEvent,
} from '@/services/data-sources/upload-sources';
import { UploadSourceResponse } from '@/services/data-sources/upload-sources';
import { UploadProgressList, UploadProgressMap } from './upload-progress-list';
import { StructuredUploadParquetCallout } from './structured-upload-help';
import {
  Collection,
  CollectionDataType,
  Source,
  StructuredUpload,
} from '@/types/source-types';
import { useGetCollectionInfo } from '@/hooks/queries/data-sources/use-get-collection-info';
import { useOrgStore } from '@/stores/org-store';
import {
  FieldOverridesForm,
  FieldOverrides,
  buildFieldOverridesPayload,
} from './field-overrides-form';
import { getUploadFileTypes } from '@/services/data-sources/get-upload-file-types';
import { Accept } from 'react-dropzone';
import { selectExcelSheets } from '@/services/data-sources/select-excel-sheets';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { CheckedState } from '@radix-ui/react-checkbox';
import { ChevronDown, Loader2 } from 'lucide-react';
import mime from 'mime';

// import { Cloud, Database, FunctionSquare, Server, Upload } from 'lucide-react';

// const dataSourceOptions = [
//   {
//     icon: Upload,
//     title: 'Custom source',
//     description: 'Upload files to create a data corpus',
//   },
//   {
//     icon: Cloud,
//     title: 's3 bucket',
//     description: 'Connect an existing s3 bucket',
//   },
//   {
//     icon: FunctionSquare,
//     title: 'Lambda function',
//     description: 'Create a custom data ingest method',
//   },
//   {
//     icon: Server,
//     title: 'API',
//     description: 'Use our API to connect your data',
//   },
//   {
//     icon: Database,
//     title: 'MCP',
//     description: 'Connect to data sources available via mcp',
//   },
// ];

export const NewDataSourceModal = ({
  collectionId,
  collection,
  collectionDataType,
  isModalOpen,
  setIsModalOpen,
  setIsLoading,
  sources,
  setSources,
}: {
  collectionId: string;
  collection?: Collection | null;
  collectionDataType?: CollectionDataType;
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  sources: Source[];
  setSources: Dispatch<SetStateAction<Source[]>>;
}) => {
  const [acceptType, setAcceptType] = useState<Accept>({});
  const [maxSize, setMaxSize] = useState(100);
  const [multipartMimeTypes, setMultipartMimeTypes] = useState<string[]>([]);
  const [maxUploadSizeMb, setMaxUploadSizeMb] = useState<number>(100);
  const [appEncryptionEnabled, setAppEncryptionEnabled] = useState(false);
  const [maxMultipartUploadSizeMb, setMaxMultipartUploadSizeMb] = useState<
    number | undefined
  >(undefined);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState<UploadProgressMap>(
    new Map(),
  );
  const abortControllerRef = useRef<AbortController | null>(null);
  const [structuredUploadQueue, setStructuredUploadQueue] = useState<
    StructuredUpload[]
  >([]);
  const [selectedSheets, setSelectedSheets] = useState<string[]>([]);
  const [isSheetModalOpen, setIsSheetModalOpen] = useState(false);
  const [isSubmittingSheets, setIsSubmittingSheets] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [rowLimitInput, setRowLimitInput] = useState('');
  const [sheetFilter, setSheetFilter] = useState('');
  const [fieldOverrides, setFieldOverrides] = useState<FieldOverrides>({});

  const notification = useNotification();
  const orgId = useOrgStore((state) => state.currentOrg?.orgid);
  const exposedAttributes = useMemo(
    () => collection?.exposed_upload_attributes ?? [],
    [collection?.exposed_upload_attributes],
  );
  const exposedCardinality = useMemo(
    () => collection?.exposed_upload_attribute_cardinality ?? null,
    [collection?.exposed_upload_attribute_cardinality],
  );
  const arrayFieldsFromCardinality = useMemo(() => {
    const set = new Set<string>();
    if (!exposedCardinality) return set;
    Object.entries(exposedCardinality).forEach(([field, value]) => {
      if (value === 'multi') set.add(field);
    });
    return set;
  }, [exposedCardinality]);
  const hasExposedAttrs = exposedAttributes.length > 0;

  const { data: collectionInfo, isLoading: isSchemaLoading } =
    useGetCollectionInfo({
      collectionId: hasExposedAttrs ? collectionId : undefined,
      orgId,
      enabled: hasExposedAttrs && isModalOpen,
    });
  // Allow multiple files for all collection types
  // For structured collections, we'll validate to prevent multiple Excel files
  const allowMultipleFiles = true;
  const currentStructuredUpload = structuredUploadQueue[0] ?? null;

  const getAllowedFileTypes = useCallback(async () => {
    const response = await getUploadFileTypes();

    if (response.detail) {
      console.warn(
        `Failed to fetch upload file types: ${response.detail ?? 'unknown error'}`,
      );
      setAcceptType({});
      setMaxSize(100);
      setMultipartMimeTypes([]);
      setMaxUploadSizeMb(100);
      setMaxMultipartUploadSizeMb(undefined);
      setAppEncryptionEnabled(false);
      return;
    }

    const policy = response.data;

    if (!policy) {
      setAcceptType({});
      setMaxSize(100);
      setMultipartMimeTypes([]);
      setMaxUploadSizeMb(100);
      setMaxMultipartUploadSizeMb(undefined);
      setAppEncryptionEnabled(false);
      return;
    }

    const {
      accepted_file_types = [],
      structured_data_file_types = [],
      max_upload_size_mb,
      multipart_upload_mime_types = [],
      max_multipart_upload_size_mb,
      app_encryption_enabled = false,
    } = policy;

    const hasStructuredTypes = structured_data_file_types.length > 0;
    const shouldUseStructuredTypes =
      collectionDataType === 'structured' && hasStructuredTypes;

    if (collectionDataType === 'structured' && !hasStructuredTypes) {
      console.warn(
        'Structured collection flagged but no structured file types provided; falling back to accepted file types.',
      );
    }

    const allowedMimeTypes = shouldUseStructuredTypes
      ? structured_data_file_types
      : accepted_file_types;

    // If any allowed type routes through the S3 multipart flow, use the
    // multipart ceiling for the dropzone's max size so large Parquet files
    // aren't pre-rejected. Per-file enforcement happens below via
    // `perFileMaxSize` so non-multipart MIMEs stay bounded by the regular cap.
    const allowsMultipartType = allowedMimeTypes.some((t) =>
      multipart_upload_mime_types.includes(t),
    );
    const effectiveMaxUploadSizeMb = max_upload_size_mb || 100;
    // Encrypted orgs cannot use multipart; cap at the single-part limit so
    // oversized files are rejected up front with the real ceiling.
    const effectiveMaxSizeMb = app_encryption_enabled
      ? effectiveMaxUploadSizeMb
      : allowsMultipartType && max_multipart_upload_size_mb
        ? max_multipart_upload_size_mb
        : effectiveMaxUploadSizeMb;

    setAppEncryptionEnabled(app_encryption_enabled);
    setMultipartMimeTypes(multipart_upload_mime_types);
    setMaxUploadSizeMb(effectiveMaxUploadSizeMb);
    setMaxMultipartUploadSizeMb(max_multipart_upload_size_mb);

    if (!allowedMimeTypes.length) {
      console.warn('No allowed file types returned from upload policy.');
      setAcceptType({});
      setMaxSize(effectiveMaxSizeMb);
      return;
    }

    const MIME_EXTENSION_OVERRIDES: Record<string, string> = {
      'application/vnd.apache.parquet': 'parquet',
    };

    const accept = allowedMimeTypes.reduce<Accept>((acc, type) => {
      const extension =
        MIME_EXTENSION_OVERRIDES[type] ?? mime.getExtension(type);

      if (!extension) {
        console.warn(
          `Unable to derive file extensions for allowed type: ${type}`,
        );
        return acc;
      }

      const uniqueExtensions = Array.from(
        new Set([...(acc[type] || []), `.${extension}`]),
      );

      return {
        ...acc,
        [type]: uniqueExtensions,
      };
    }, {} as Accept);

    setAcceptType(accept);
    setMaxSize(effectiveMaxSizeMb);
  }, [collectionDataType]);

  useEffect(() => {
    getAllowedFileTypes();
  }, [getAllowedFileTypes]);

  useEffect(() => {
    if (currentStructuredUpload) {
      const defaultSheets = currentStructuredUpload.sheets.map(
        (sheet) => sheet.name,
      );
      setSelectedSheets(defaultSheets);
      setIsSheetModalOpen(true);
      setRowLimitInput('');
      setIsAdvancedOpen(false);
      setSheetFilter('');
    } else {
      setSelectedSheets([]);
      setIsSheetModalOpen(false);
      setRowLimitInput('');
      setIsAdvancedOpen(false);
      setSheetFilter('');
    }
  }, [currentStructuredUpload]);

  const handleDropAccepted = async (files: File[]) => {
    const filesToUpload = allowMultipleFiles ? files : files.slice(0, 1);

    setIsLoading(true);
    setIsUploading(true);

    // Seed the progress map so the list renders immediately with all files
    // queued, before any network work starts.
    const initial: UploadProgressMap = new Map(
      filesToUpload.map((f) => [
        fileIdOf(f),
        {
          fileId: fileIdOf(f),
          filename: f.name,
          size: f.size,
          phase: 'queued' as const,
          percent: 0,
        },
      ]),
    );
    setUploadProgress(initial);

    const controller = new AbortController();
    abortControllerRef.current = controller;

    const handleProgress = (event: UploadProgressEvent) => {
      setUploadProgress((prev) => {
        const next = new Map(prev);
        next.set(event.fileId, event);
        return next;
      });
    };

    const overridesPayload = hasExposedAttrs
      ? buildFieldOverridesPayload(
          exposedAttributes,
          fieldOverrides,
          collectionInfo?.payload_schema,
        )
      : undefined;

    await uploadSources({
      files: filesToUpload,
      collection: collectionId,
      collectionDataType,
      fieldOverrides: overridesPayload,
      multipartMimeTypes,
      appEncryptionEnabled,
      signal: controller.signal,
      onProgress: handleProgress,
    })
      .then((res: UploadSourceResponse) => {
        const uploadData = res.data;

        if (!uploadData) {
          notification.error(res.detail || 'Failed to upload data source');
          return;
        }

        if (uploadData.uploaded_files?.length) {
          const newSources = uploadData.uploaded_files.map((file) => ({
            filename: file.file_info.filename,
            size_mb: Number(file.file_info.file_size_mb),
            content_type: file.file_info.mime_type,
            s3_url: file.s3_url,
            original_filename: file.original_filename,
            key: null,
            last_modified: new Date().toISOString(),
            metadata: null,
            size: null,
            upload_timestamp: null,
            tags: {
              indexing_status: file.ingestion_status || 'pending',
              ingestion_method: file.ingestion_method as
                | 'celery'
                | 'lambda'
                | undefined,
            },
            // Celery ingestion tracking fields
            celery_task_id: file.celery_task_id,
            ingestion_method: file.ingestion_method as
              | 'celery'
              | 'lambda'
              | undefined,
            ingestion_status: file.ingestion_status,
            celery_task_status: (file.ingestion_status === 'queued'
              ? 'PENDING'
              : undefined) as
              | 'PENDING'
              | 'STARTED'
              | 'SUCCESS'
              | 'FAILURE'
              | undefined,
            celery_status_last_checked: Date.now(),
          }));

          setSources((prev) => [...prev, ...newSources]);
        }

        notification.success('Data source uploaded successfully');

        const structuredUploads = uploadData.structured_uploads ?? [];
        const uploadsRequiringSelection = structuredUploads.filter(
          (upload) =>
            upload.requires_sheet_selection && upload.sheets?.length > 0,
        );

        structuredUploads
          .filter(
            (upload) =>
              upload.requires_sheet_selection && !(upload.sheets?.length > 0),
          )
          .forEach((upload) => {
            console.warn(
              `Structured upload ${upload.excel_object_key} requires sheet selection but no sheets were provided by the backend response.`,
            );
          });

        if (uploadsRequiringSelection.length) {
          setStructuredUploadQueue((prev) => [
            ...prev,
            ...uploadsRequiringSelection,
          ]);
        } else if (
          structuredUploads.some((upload) => !upload.requires_sheet_selection)
        ) {
          notification.success(
            'Excel workbook converted successfully. Files will appear shortly.',
          );
        }

        setIsModalOpen(false);
        setSelectedFiles([]);
      })
      .catch((error: Error) => {
        console.error(error);
        notification.error('Failed to upload data source');
      })
      .finally(() => {
        setIsLoading(false);
        setIsUploading(false);
        abortControllerRef.current = null;
      });
  };

  const handleCancelUpload = () => {
    if (
      abortControllerRef.current &&
      !abortControllerRef.current.signal.aborted
    ) {
      abortControllerRef.current.abort();
    }
  };

  const handleFilesChange = useCallback(
    (files: File[]) => {
      // Per-file size enforcement: multipart ceiling only applies to
      // multipart-eligible MIME types. Others cap at `max_upload_size_mb`.
      const oversizeFiles: File[] = [];
      const okFiles = files.filter((file) => {
        const isMultipart =
          collectionDataType === 'unstructured' ||
          multipartMimeTypes.includes(file.type);
        const ceiling =
          isMultipart && maxMultipartUploadSizeMb
            ? maxMultipartUploadSizeMb
            : maxUploadSizeMb;
        const ceilingBytes = ceiling * 1024 * 1024;
        if (file.size > ceilingBytes) {
          oversizeFiles.push(file);
          return false;
        }
        return true;
      });

      if (oversizeFiles.length > 0) {
        notification.error(
          `${oversizeFiles.length} file(s) exceed the size limit for their type and were removed.`,
        );
      }

      // For structured collections, validate Excel file restrictions
      if (collectionDataType === 'structured' && okFiles.length > 1) {
        const excelFiles = okFiles.filter((file) => {
          const ext = file.name.toLowerCase();
          return ext.endsWith('.xlsx') || ext.endsWith('.xls');
        });

        // If multiple Excel files, reject all but the first
        if (excelFiles.length > 1) {
          notification.error(
            'Only one Excel workbook can be uploaded at a time. Multiple CSV files are allowed.',
          );
          setSelectedFiles([excelFiles[0]]);
          return;
        }

        // If 1 Excel file mixed with other files, reject the other files
        if (excelFiles.length === 1 && okFiles.length > 1) {
          notification.error(
            'Excel workbooks must be uploaded separately. Please upload the Excel file alone or select only CSV files.',
          );
          setSelectedFiles([excelFiles[0]]);
          return;
        }
      }

      // All validations passed, set the files
      setSelectedFiles(okFiles);
    },
    [
      collectionDataType,
      notification,
      multipartMimeTypes,
      maxMultipartUploadSizeMb,
      maxUploadSizeMb,
    ],
  );

  const confirmUpload = async () => {
    if (selectedFiles.length === 0) {
      return;
    }

    setIsLoading(true);
    try {
      await handleDropAccepted(selectedFiles);
    } finally {
      setIsLoading(false);
    }
  };

  const workbookDisplayName = useMemo(() => {
    if (!currentStructuredUpload) return '';
    const segments = currentStructuredUpload.excel_object_key.split('/');
    return segments.at(-1) || currentStructuredUpload.excel_object_key;
  }, [currentStructuredUpload]);

  const updateSelectedSheets = useCallback(
    (updater: (current: Set<string>) => Set<string>) => {
      setSelectedSheets((prev) => {
        const base = new Set(prev);
        const next = updater(base);

        if (!currentStructuredUpload) {
          return Array.from(next);
        }

        return currentStructuredUpload.sheets
          .map((sheet) => sheet.name)
          .filter((name) => next.has(name));
      });
    },
    [currentStructuredUpload],
  );

  const handleSheetToggle = (sheetName: string, checked: CheckedState) => {
    const isChecked = checked === true || checked === 'indeterminate';

    updateSelectedSheets((current) => {
      const next = new Set(current);
      if (isChecked) {
        next.add(sheetName);
      } else {
        next.delete(sheetName);
      }
      return next;
    });
  };

  const handleSelectAllSheets = (checked: boolean, sheetNames: string[]) => {
    updateSelectedSheets((current) => {
      const next = new Set(current);
      sheetNames.forEach((name) => {
        if (checked) {
          next.add(name);
        } else {
          next.delete(name);
        }
      });
      return next;
    });
  };

  const filteredSheets = useMemo(() => {
    if (!currentStructuredUpload) return [];
    const query = sheetFilter.trim().toLowerCase();
    if (!query) return currentStructuredUpload.sheets;

    return currentStructuredUpload.sheets.filter((sheet) =>
      sheet.name.toLowerCase().includes(query),
    );
  }, [currentStructuredUpload, sheetFilter]);

  const filteredSheetNames = useMemo(
    () => filteredSheets.map((sheet) => sheet.name),
    [filteredSheets],
  );

  const selectedFilteredCount = useMemo(() => {
    if (!filteredSheetNames.length) return 0;
    const selectedSet = new Set(selectedSheets);
    return filteredSheetNames.reduce(
      (count, name) => (selectedSet.has(name) ? count + 1 : count),
      0,
    );
  }, [filteredSheetNames, selectedSheets]);

  const sheetSelectAllState: CheckedState = filteredSheetNames.length
    ? selectedFilteredCount === filteredSheetNames.length
      ? true
      : selectedFilteredCount > 0
        ? 'indeterminate'
        : false
    : false;

  const advanceStructuredUploadQueue = () => {
    setStructuredUploadQueue((prev) => prev.slice(1));
  };

  const handleSheetSelectionSkip = () => {
    if (!currentStructuredUpload) return;
    notification.info(
      'Sheet selection skipped. You can re-upload to try again.',
    );
    advanceStructuredUploadQueue();
  };

  const handleSheetSelectionConfirm = async () => {
    if (!currentStructuredUpload) return;

    if (!selectedSheets.length) {
      notification.error('Select at least one sheet to continue.');
      return;
    }

    const trimmedRowLimit = rowLimitInput.trim();
    let parsedRowLimit: number | undefined;

    if (trimmedRowLimit) {
      const candidate = Number(trimmedRowLimit);

      if (!Number.isInteger(candidate) || candidate <= 0) {
        notification.error('Enter a valid row limit greater than zero.');
        return;
      }

      parsedRowLimit = candidate;
    }

    setIsSubmittingSheets(true);
    try {
      const orderedSelection = currentStructuredUpload.sheets
        .map((sheet) => sheet.name)
        .filter((name) => selectedSheets.includes(name));

      if (!orderedSelection.length) {
        notification.error('Select at least one valid sheet to continue.');
        return;
      }

      const response = await selectExcelSheets({
        collectionId,
        excelObjectKey: currentStructuredUpload.excel_object_key,
        sheets: orderedSelection,
        rowLimit: parsedRowLimit,
      });

      if (response.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success(
        'Sheet selection submitted. Converted files will appear shortly.',
      );
      advanceStructuredUploadQueue();
    } catch (error) {
      console.error('Error selecting Excel sheets:', error);
      notification.error('Failed to submit sheet selection');
    } finally {
      setIsSubmittingSheets(false);
    }
  };

  const handleSheetModalToggle = (open: boolean) => {
    if (!open && currentStructuredUpload && !isSubmittingSheets) {
      handleSheetSelectionSkip();
    }
  };

  const handleModalToggle = (open: boolean) => {
    // While uploads are in flight, the Modal component already prevents ESC
    // / overlay close via `preventClose`. This guard is a belt-and-braces
    // defense for any programmatic close attempts.
    if (!open && isUploading) return;
    setIsModalOpen(open);
    if (!open) {
      setSelectedFiles([]);
      setFieldOverrides({});
      setUploadProgress(new Map());
    }
  };

  return (
    <>
      <Modal
        isDialogOpen={isModalOpen}
        setIsDialogOpen={handleModalToggle}
        preventClose={isUploading}
        hideCloseButton={isUploading}
        title={
          <span id='data-sources-new-data-source-title'>New data source</span>
        }
        footer={
          <div className='flex items-center justify-end gap-2'>
            <Button
              id='data-sources-new-data-source-cancel'
              variant='outline'
              onClick={() => handleModalToggle(false)}
              disabled={isUploading}
            >
              Cancel
            </Button>
            <Button
              onClick={confirmUpload}
              disabled={isUploading || selectedFiles.length === 0}
            >
              Upload
            </Button>
          </div>
        }
      >
        <div className='flex flex-col gap-4 mt-4 min-w-0'>
          {collectionDataType === 'structured' && !isUploading && (
            <StructuredUploadParquetCallout selectedFiles={selectedFiles} />
          )}
          {isUploading && uploadProgress.size > 0 && (
            <UploadProgressList
              progressMap={uploadProgress}
              onCancel={handleCancelUpload}
              canCancel
            />
          )}
          <Dropzone
            mode='manual'
            acceptType={acceptType}
            multiple={allowMultipleFiles}
            maxSize={maxSize}
            onFilesChange={handleFilesChange}
            isLoading={isUploading}
            existingFiles={(sources || [])
              .map(
                (source) =>
                  source.original_filename ||
                  source.metadata?.['original-filename'],
              )
              .filter((name): name is string => !!name)
              .map((name) => ({ name }))}
            fileNameRegex={/^[a-zA-Z0-9_. ()-]+$/}
            fileNameErrorMessage='File name contains invalid characters. Must only consist of letters, numbers, spaces, underscores, hyphens, dots, and parentheses.'
          />

          {hasExposedAttrs && (
            <div className='border-t pt-4 space-y-3'>
              <div>
                <h3 className='text-sm font-medium'>Field Overrides</h3>
                <p className='text-xs text-muted-foreground'>
                  Values entered here apply to every file in this batch.
                </p>
              </div>
              {isSchemaLoading ? (
                <p className='text-sm text-muted-foreground'>
                  Loading field schema...
                </p>
              ) : (
                <FieldOverridesForm
                  exposedAttributes={exposedAttributes}
                  payloadSchema={collectionInfo?.payload_schema}
                  values={fieldOverrides}
                  onChange={setFieldOverrides}
                  disabled={isUploading}
                  arrayFields={arrayFieldsFromCardinality}
                />
              )}
            </div>
          )}
        </div>
      </Modal>

      {currentStructuredUpload && (
        <Modal
          isDialogOpen={isSheetModalOpen}
          setIsDialogOpen={handleSheetModalToggle}
          title={<span>Select sheets to ingest</span>}
          footer={
            <div className='flex items-center justify-end gap-2'>
              <Button
                variant='outline'
                onClick={handleSheetSelectionSkip}
                disabled={isSubmittingSheets}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSheetSelectionConfirm}
                disabled={isSubmittingSheets || selectedSheets.length === 0}
              >
                {isSubmittingSheets ? (
                  <span className='flex items-center gap-2'>
                    <Loader2 className='h-4 w-4 animate-spin' />
                    Submitting...
                  </span>
                ) : (
                  'Confirm'
                )}
              </Button>
            </div>
          }
        >
          <div className='space-y-4 mt-2'>
            <div className='space-y-1'>
              <p className='text-sm text-muted-foreground'>
                Choose the sheets from
                <span className='font-medium text-foreground'>{` ${workbookDisplayName} `}</span>
                to convert into structured data. Only selected sheets will be
                processed.
              </p>
            </div>

            {currentStructuredUpload.sheets.length > 0 && (
              <Input
                value={sheetFilter}
                onChange={(event) => setSheetFilter(event.target.value)}
                placeholder='Filter sheets by name'
                disabled={isSubmittingSheets}
              />
            )}

            {currentStructuredUpload.sheets.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No sheets returned for this workbook.
              </p>
            ) : filteredSheets.length === 0 ? (
              <p className='text-sm text-muted-foreground'>
                No sheets match your filter. Try a different search term.
              </p>
            ) : (
              <>
                <label className='flex items-center gap-2 text-sm text-muted-foreground cursor-pointer'>
                  <Checkbox
                    checked={sheetSelectAllState}
                    onCheckedChange={(checked) =>
                      handleSelectAllSheets(
                        checked === true,
                        filteredSheetNames,
                      )
                    }
                    disabled={isSubmittingSheets}
                    aria-label='Select all filtered sheets'
                  />
                  <span>
                    Select all
                    {sheetFilter.trim()
                      ? ` (${filteredSheets.length} filtered)`
                      : ` (${filteredSheets.length})`}
                  </span>
                </label>
                <div className='max-h-64 overflow-y-auto pr-1 space-y-2'>
                  {filteredSheets.map((sheet) => {
                    const checkboxId = `${sheet.index}-${sheet.name}`;
                    const isChecked = selectedSheets.includes(sheet.name);
                    return (
                      <div
                        key={checkboxId}
                        className='flex items-center gap-3 rounded-md border border-border px-3 py-2'
                      >
                        <Checkbox
                          id={checkboxId}
                          checked={isChecked}
                          onCheckedChange={(checked) =>
                            handleSheetToggle(sheet.name, checked)
                          }
                          disabled={isSubmittingSheets}
                        />
                        <Label
                          htmlFor={checkboxId}
                          className='flex-1 cursor-pointer text-sm font-medium'
                        >
                          {sheet.name}
                        </Label>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            <Collapsible
              open={isAdvancedOpen}
              onOpenChange={setIsAdvancedOpen}
              className='border border-border/60 rounded-md px-3 py-2'
            >
              <CollapsibleTrigger asChild>
                <Button
                  variant='ghost'
                  type='button'
                  className='flex w-full items-center justify-between px-0 text-sm font-medium'
                >
                  Advanced options
                  <ChevronDown
                    className={`h-4 w-4 transition-transform duration-200 ${isAdvancedOpen ? 'rotate-180' : ''}`}
                  />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className='mt-3 space-y-2'>
                  <Label
                    htmlFor='row-limit-input'
                    className='text-sm font-medium'
                  >
                    Row limit per sheet (optional)
                  </Label>
                  <Input
                    id='row-limit-input'
                    type='number'
                    min={1}
                    inputMode='numeric'
                    placeholder='Enter a row limit'
                    value={rowLimitInput}
                    onChange={(event) => setRowLimitInput(event.target.value)}
                    disabled={isSubmittingSheets}
                  />
                  <p className='text-xs text-muted-foreground'>
                    Applies the row limit to every selected sheet during CSV
                    conversion.
                  </p>
                </div>
              </CollapsibleContent>
            </Collapsible>

            {selectedSheets.length === 0 && (
              <p className='text-sm text-red-500'>
                Select at least one sheet to continue.
              </p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
};
